import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { api } from '../lib/api';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

export default function VerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuth, loadUser } = useAuthStore();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyToken = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setMessage('No verification token provided');
        return;
      }

      try {
        const response = await api.verifyMagicLink(token);
        // Store the JWT token first so subsequent API calls are authenticated
        localStorage.setItem('auth_token', response.token);
        // Now load the full user profile (with city, country, etc.) from /me
        await loadUser();
        setStatus('success');
        setMessage('Successfully authenticated!');
        setTimeout(() => navigate('/'), 1000);
      } catch (err: any) {
        setStatus('error');
        setMessage(err.response?.data?.error || 'Verification failed');
      }
    };

    verifyToken();
  }, [searchParams, navigate, setAuth, loadUser]);

  return (
    <div className="card text-center">
      {status === 'loading' && (
        <>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying your magic link...</p>
        </>
      )}

      {status === 'success' && (
        <>
          <CheckCircleIcon className="h-12 w-12 text-green-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">{message}</h2>
          <p className="text-gray-600">Redirecting to your dashboard...</p>
        </>
      )}

      {status === 'error' && (
        <>
          <XCircleIcon className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Verification Failed</h2>
          <p className="text-gray-600 mb-6">{message}</p>
          <button
            onClick={() => navigate('/login')}
            className="btn-primary"
          >
            Return to login
          </button>
        </>
      )}
    </div>
  );
}
